import React, { useCallback, useEffect, useState } from 'react';
import { formatAmount, money } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  ConfirmDialog,
  DescriptionList,
  EmptyState,
  Input,
  KpiTile,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  Timeline,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { purchaseOrderPath, supplierReturnPath, goodsReceiptPath } from '../../lib/slug';
import {
  returnsApi,
  returnStatusMeta,
  creditNoteStatusMeta,
  returnReasonLabel,
  CREDIT_NOTE_STATUSES,
} from './returnsApi';
import CommentComposer from '../comments/CommentComposer';
import { buildCommentTimelineItem, useCommentAnchor } from '../comments/CommentTimeline';
import { useAuth } from '../../auth/AuthContext';
import '../requisitions/requisitions.css';

const lineValue = (l) => (l.unitPrice ? `${l.unitPrice.currency} ${formatAmount(Number(l.unitPrice.amount) * Number(l.quantity))}` : '—');

const ACTIVITY_META = {
  Created: { tone: 'brand', icon: <Icon.FileText size={14} /> },
  Posted: { tone: 'success', icon: <Icon.ArrowUpCircle size={14} /> },
  Cancelled: { tone: 'warning', icon: <Icon.Ban size={14} /> },
  CreditNoteRecorded: { tone: 'brand', icon: <Icon.Receipt size={14} /> },
};

export default function ReturnDetailPage() {
  const { id: routeRef } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const { hasPermission } = useProperty();
  const { user } = useAuth();
  const canManage = hasPermission('returns.manage');

  const [ret, setRet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [activity, setActivity] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const anchorId = useCommentAnchor(activity);
  const [busy, setBusy] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Credit-note capture form
  const [cnRef, setCnRef] = useState('');
  const [cnAmount, setCnAmount] = useState('');
  const [cnStatus, setCnStatus] = useState('Expected');
  const [cnBusy, setCnBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRet(await returnsApi.get(routeRef));
    } catch (err) {
      setError(err?.message || t('Return not found.'));
    } finally {
      setLoading(false);
    }
  }, [routeRef, t]);

  const loadActivity = useCallback(async (id) => {
    try {
      const res = await returnsApi.listActivity(id, { pageSize: 100 });
      setActivity(res.items || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (ret?.id) loadActivity(ret.id); }, [ret?.id, loadActivity]);

  useEffect(() => {
    let active = true;
    catalogApi.listUnits().then((u) => { if (active) setUnits(u || []); }).catch(() => {});
    catalogApi.listLocations().then((l) => { if (active) setLocations(l || []); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!ret?.id) return;
    const canonical = supplierReturnPath(ret);
    if (`/returns/${routeRef}` !== canonical) navigate(canonical, { replace: true });
  }, [ret, routeRef, navigate]);

  // Seed the credit-note form from the loaded return.
  useEffect(() => {
    if (!ret) return;
    setCnRef(ret.creditNoteReference || '');
    setCnStatus(ret.creditNoteStatus && ret.creditNoteStatus !== 'None' ? ret.creditNoteStatus : 'Expected');
    setCnAmount(
      ret.creditAmount ? String(ret.creditAmount.amount)
        : ret.total ? String(ret.total.amount) : ''
    );
  }, [ret]);

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;
  const locationName = (lid) => locations.find((l) => l.id === lid)?.name;

  const doPost = async () => {
    setBusy(true);
    try {
      const posted = await returnsApi.post(ret.id);
      setPostOpen(false);
      setRet(posted);
      loadActivity(ret.id);
      toast.success(t('Return {number} posted', { number: posted.number }));
    } catch (err) {
      toast.error(t('Could not post return'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      const cancelled = await returnsApi.cancel(ret.id, cancelReason.trim() || null);
      setCancelOpen(false);
      setCancelReason('');
      setRet(cancelled);
      loadActivity(ret.id);
      toast.success(ret.status === 'Posted' ? t('Return cancelled and reversed') : t('Draft return cancelled'));
    } catch (err) {
      toast.error(t('Could not cancel return'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const recordCreditNote = async () => {
    setCnBusy(true);
    try {
      const updated = await returnsApi.recordCreditNote(ret.id, {
        reference: cnRef.trim() || null,
        amount: cnAmount === '' ? null : Number(cnAmount),
        status: cnStatus,
      });
      setRet(updated);
      loadActivity(ret.id);
      toast.success(t('Credit note updated'));
    } catch (err) {
      toast.error(t('Could not record credit note'), { description: err?.message });
    } finally {
      setCnBusy(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }
  if (error || !ret) {
    return (
      <PageContainer size="full">
        <EmptyState icon={<Icon.CircleAlert size={28} />} title={t("Couldn't load return")} description={error || t('Not found.')}
          action={<Button variant="secondary" onClick={() => navigate('/returns')}>{t('Back to returns')}</Button>} />
      </PageContainer>
    );
  }

  const statusMeta = returnStatusMeta(ret.status);
  const cnMeta = creditNoteStatusMeta(ret.creditNoteStatus);
  const isDraft = ret.status === 'Draft';
  const isPosted = ret.status === 'Posted';
  const isCancelled = ret.status === 'Cancelled';

  const headerActions = canManage && !isCancelled ? (
    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
      {isDraft && (
        <Button variant="primary" size="sm" leadingIcon={<Icon.ArrowUpCircle size={14} />} onClick={() => setPostOpen(true)}>{t('Post return')}</Button>
      )}
      <Button variant="destructive" size="sm" leadingIcon={<Icon.Ban size={14} />} onClick={() => setCancelOpen(true)}>
        {isPosted ? t('Cancel & reverse') : t('Discard draft')}
      </Button>
    </div>
  ) : null;

  const overviewItems = [
    { term: t('Status'), description: <Badge tone={statusMeta.tone} dot>{t(statusMeta.label)}</Badge> },
    {
      term: t('Purchase order'),
      description: ret.purchaseOrderNumber
        ? <Button variant="link" size="sm" onClick={() => navigate(purchaseOrderPath({ number: ret.purchaseOrderNumber, id: ret.purchaseOrderId }))}>{ret.purchaseOrderNumber}</Button>
        : '—',
    },
    {
      term: t('Goods receipt'),
      description: ret.goodsReceiptNumber
        ? <Button variant="link" size="sm" onClick={() => navigate(goodsReceiptPath({ number: ret.goodsReceiptNumber, id: ret.goodsReceiptId }))}>{ret.goodsReceiptNumber}</Button>
        : '—',
    },
    { term: t('Supplier'), description: ret.supplierName || '—' },
    { term: t('Reason'), description: t(returnReasonLabel(ret.reason)) },
    { term: t('Returned from'), description: locationName(ret.locationId) || '—' },
    { term: t('Return date'), description: ret.returnDate ? new Date(ret.returnDate).toLocaleDateString() : '—' },
    { term: ret.status === 'Posted' ? t('Posted by') : t('Created by'), description: (isPosted ? ret.postedByName : ret.createdByName) || '—' },
  ];

  const renderLine = (l) => {
    const uom = uomCode(l.uomId) ? ` ${uomCode(l.uomId)}` : '';
    const coding = [l.costCenter, l.glAccountCode].filter(Boolean).join(' / ');
    const sub = [l.sku, coding].filter(Boolean).join(' · ');
    return (
      <div className="grn-line" key={l.lineId}>
        <div className="grn-line__item">
          <div className="grn-line__name">{l.description}</div>
          {sub && <div className="grn-line__sub">{sub}</div>}
          <div className="grn-line__issue">
            <Badge tone="warning">{t(returnReasonLabel(l.reason))}</Badge>
            {l.notes && <span className="grn-line__issue-note">“{l.notes}”</span>}
          </div>
        </div>
        <div className="grn-line__qty">
          <div className="grn-line__stat">
            <span className="grn-line__stat-label">{t('Returned')}</span>
            <span className="grn-line__stat-value">{l.quantity}{uom}</span>
          </div>
          <div className="grn-line__stat">
            <span className="grn-line__stat-label">{t('Unit price')}</span>
            <span className="grn-line__stat-value">{money(l.unitPrice)}</span>
          </div>
          <div className="grn-line__stat">
            <span className="grn-line__stat-label">{t('Value')}</span>
            <span className="grn-line__stat-value">{lineValue(l)}</span>
          </div>
        </div>
      </div>
    );
  };

  const activityItems = activity.map((a) => {
    if (a.type === 'Commented') {
      return buildCommentTimelineItem({
        activity: a,
        apiBasePath: `/supplier-returns/${ret.id}`,
        attachmentsBasePath: `/supplier-returns/${ret.id}/attachments`,
        currentUserId: user?.id,
        editingCommentId,
        setEditingCommentId,
        onChanged: () => loadActivity(ret.id),
        highlightId: anchorId,
        t,
      });
    }

    const meta = ACTIVITY_META[a.type] || { tone: 'default', icon: <Icon.Activity size={14} /> };
    const detail = a.type === 'CreditNoteRecorded'
      ? [a.metadata?.reference && t('Ref {reference}', { reference: a.metadata.reference }), a.metadata?.amount, a.metadata?.status].filter(Boolean).join(' · ')
      : a.metadata?.value || a.metadata?.reason || undefined;
    return {
      id: a.id,
      tone: meta.tone,
      icon: meta.icon,
      title: `${a.actorName || t('Someone')} ${a.summary}`,
      meta: new Date(a.createdAt).toLocaleString(),
      description: detail || undefined,
    };
  });

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/returns')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Returns')}
      </Button>

      <PageHeader
        title={isDraft ? t('Draft return') : t('Return {number}', { number: ret.number })}
        description={<span className="req-detail__number">{ret.purchaseOrderNumber}</span>}
        icon={<Icon.ArrowUpCircle size={22} />}
        actions={headerActions}
      />

      {isDraft && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="info">
            {t('This return is a draft — no stock, purchase-order or history changes have been made. Post it to reverse the stock and update the order.')}
          </Banner>
        </div>
      )}

      {isCancelled && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="danger">
            {t('This return was cancelled')}{ret.cancelledByName ? ` ${t('by {name}', { name: ret.cancelledByName })}` : ''}{ret.cancelledAt ? ` ${t('on {date}', { date: new Date(ret.cancelledAt).toLocaleString() })}` : ''}. {t('Any stock and purchase-order effects were reversed.')}{ret.cancelledReason ? ` ${t('Reason: {reason}', { reason: ret.cancelledReason })}` : ''}
          </Banner>
        </div>
      )}

      <div className="req-kpis">
        <KpiTile label={t('Return value')} value={money(ret.total)} />
        <KpiTile label={t('Lines')} value={ret.lineCount} />
        <KpiTile label={t('Reason')} value={t(returnReasonLabel(ret.reason))} />
        <KpiTile label={t('Credit note')} value={t(cnMeta.label)} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Card>
          <Card.Body>
            <DescriptionList items={overviewItems} layout="stacked" columns={2} />
          </Card.Body>
        </Card>

        {ret.notes && (
          <Card>
            <Card.Header><Card.Title>{t('Notes')}</Card.Title></Card.Header>
            <Card.Body>{ret.notes}</Card.Body>
          </Card>
        )}

        <Card>
          <Card.Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Card.Title>{t('Credit note')}</Card.Title>
            <Badge tone={cnMeta.tone} dot>{t(cnMeta.label)}</Badge>
          </Card.Header>
          <Card.Body>
            {isCancelled ? (
              <DescriptionList
                layout="stacked"
                columns={2}
                items={[
                  { term: t('Reference'), description: ret.creditNoteReference || '—' },
                  { term: t('Amount'), description: ret.creditAmount ? money(ret.creditAmount) : '—' },
                ]}
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                <div className="pp-cell-muted">
                  {t("Track the supplier's credit note for this return. Default amount is the return value; adjust it to match what the supplier actually credits.")}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-3)' }}>
                  <Input label={t('Credit note reference')} value={cnRef} onChange={(e) => setCnRef(e.target.value)} placeholder={t('e.g. CN-10293')} disabled={!canManage} />
                  <Input label={t('Amount ({currency})', { currency: ret.currency })} type="number" min="0" step="any" value={cnAmount} onChange={(e) => setCnAmount(e.target.value)} disabled={!canManage} style={{ textAlign: 'right' }} />
                  <div className="ps-field">
                    <span className="ps-field__label">{t('Status')}</span>
                    <Select value={cnStatus} onChange={setCnStatus} ariaLabel={t('Credit note status')} options={CREDIT_NOTE_STATUSES.map((s) => ({ value: s.value, label: t(s.label) }))} disabled={!canManage} />
                  </div>
                </div>
                <div>
                  <Button variant="primary" size="sm" loading={cnBusy} disabled={!canManage} leadingIcon={<Icon.Receipt size={14} />} onClick={recordCreditNote}>
                    {t('Save credit note')}
                  </Button>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <Card.Title>{t('Returned lines')}</Card.Title>
            <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
              {isPosted
                ? t('These quantities were reversed out of stock and deducted from the purchase order.')
                : t('These quantities will be reversed out of stock when the return is posted.')}
            </div>
          </Card.Header>
          <Card.Body>
            <div className="grn-lines">
              {(ret.lines || []).map(renderLine)}
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <Card.Title>{t('Activity')}</Card.Title>
            <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
              {t('Audit trail for this return — who did what and when.')}
            </div>
          </Card.Header>
          <Card.Body>
            <CommentComposer
              basePath={`/supplier-returns/${ret.id}`}
              onPosted={() => loadActivity(ret.id)}
            />
            {activityItems.length === 0 ? (
              <EmptyState icon={<Icon.Activity size={26} />} title={t('No activity yet')} description={t('Posting, cancellation and credit-note events will appear here.')} />
            ) : (
              <Timeline items={activityItems} className="req-activity__timeline" />
            )}
          </Card.Body>
        </Card>
      </div>

      <ConfirmDialog
        open={postOpen}
        title={t('Post return')}
        description={t("Posting reverses the returned quantities out of stock, decrements the purchase order's received quantities and writes a negative purchase-history entry. You can still cancel it afterwards.")}
        confirmLabel={t('Post return')}
        tone="primary"
        loading={busy}
        onConfirm={doPost}
        onCancel={() => (busy ? null : setPostOpen(false))}
      />

      <ConfirmDialog
        open={cancelOpen}
        title={isPosted ? t('Cancel & reverse return') : t('Discard draft return')}
        description={isPosted
          ? t("This restores the returned stock and the purchase order's received quantities, and deletes the negative purchase-history records.")
          : t('This voids the draft return. Nothing has been committed, so there is nothing to reverse.')}
        confirmLabel={isPosted ? t('Cancel return') : t('Discard draft')}
        tone="danger"
        loading={busy}
        onConfirm={doCancel}
        onCancel={() => (busy ? null : setCancelOpen(false))}
      >
        <Textarea
          rows={2}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder={t('Reason (optional)')}
        />
      </ConfirmDialog>
    </PageContainer>
  );
}
